import { ValantisStHypeAbi } from '../abis/valantis'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Valantis wstHYPE reader.
 *
 * Two calls: `wstHYPE.totalSupply()` + `stHYPE.balancePerShare()`
 * (1e18-scaled HYPE-per-share). wstHYPE wraps the same underlying
 * state as stHYPE, so the rate getter on the stHYPE contract works
 * for both.
 *
 * `entry.readerExtras.valantisStHype` pins the stHYPE address. Fall
 * back to `offChain`-style placeholder if absent.
 */
export const readerValantisWstHype = (entry: LstRegistryEntry): LstReader => {
  const stHype = entry.readerExtras?.valantisStHype
  if (!stHype) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: stHype, name: 'balancePerShare', params: [] },
    ],
    abis: [TotalSupplyAbi, ValantisStHypeAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
      }
    },
  }
}
